import { usePageRefreshKey } from '../../../context/PageRefreshContext';
import { useState, useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import VendorSettingLayout from './VendorSettingLayout';
import { getVendorCategories, createVendorCategory, updateVendorCategory, deleteVendorCategory } from '../../../redux/actions/SocietyPortal/VendorAction/VendorAction';
import { selectedSocietyStorer } from '../../../utils/webStorer';

const EMPTY_FORM = { name: '', description: '' };

const VendorSettingCategories = () => {
    const dispatch = useDispatch();
    const {
        vendorCategoriesData,
        vendorCategoriesLoading,
        createVendorCategoryLoading,
        updateVendorCategoryLoading,
        deleteVendorCategoryLoading,
    } = useSelector(state => state.VendorReducers);
    const { loginData } = useSelector(state => state.loginReducer);

    const [modalMode, setModalMode] = useState(null); // null | 'add' | 'edit'
    const [editingCategory, setEditingCategory] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);

    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        dispatch(getVendorCategories());
    }, [dispatch, refreshKey]);

    const categories = useMemo(() => {
        if (!Array.isArray(vendorCategoriesData)) return [];
        return vendorCategoriesData.map(cat => ({
            id: cat.category_id || cat.id,
            name: cat.label || cat.name || cat.category_name,
            icon: cat.icon || 'mdi:store',
            description: cat.description || '',
            vendorCount: cat.vendor_count || cat.vendorCount || 0,
        }));
    }, [vendorCategoriesData]);

    const handleOpenAdd = () => {
        setForm(EMPTY_FORM);
        setEditingCategory(null);
        setModalMode('add');
    };

    const handleOpenEdit = (category) => {
        setForm({ name: category.name, description: category.description });
        setEditingCategory(category);
        setModalMode('edit');
    };

    const handleCloseModal = () => {
        setModalMode(null);
        setEditingCategory(null);
        setForm(EMPTY_FORM);
    };

    const handleSave = () => {
        if (!form.name.trim()) return;
        const societyId = selectedSocietyStorer.getSocietyId();
        if (modalMode === 'edit') {
            dispatch(updateVendorCategory({
                categoryId: editingCategory.id,
                name: form.name,
                description: form.description,
                societyId,
                onSuccess: () => {
                    handleCloseModal();
                    dispatch(getVendorCategories());
                },
            }));
        } else {
            dispatch(createVendorCategory({
                name: form.name,
                description: form.description,
                userId: loginData?.user?.user_id,
                societyId,
                onSuccess: () => {
                    handleCloseModal();
                    dispatch(getVendorCategories());
                },
            }));
        }
    };

    const handleDeleteConfirm = (id) => {
        const societyId = selectedSocietyStorer.getSocietyId();
        dispatch(deleteVendorCategory({
            categoryId: id,
            societyId,
            onSuccess: () => {
                setDeleteConfirmId(null);
                dispatch(getVendorCategories());
            },
        }));
    };

    const isSaveLoading = createVendorCategoryLoading || updateVendorCategoryLoading;

    return (
        <VendorSettingLayout>
            <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                    <h2 className="tw-text-sm tw-font-semibold tw-text-gray-800">Vendor Categories</h2>
                    <button
                        onClick={handleOpenAdd}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-bg-blue-600 tw-text-white tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-blue-700"
                    >
                        <Icon icon="mdi:plus" /> Add Category
                    </button>
                </div>

                {vendorCategoriesLoading ? (
                    <div className="tw-flex tw-items-center tw-justify-center tw-py-10">
                        <Icon icon="mdi:loading" className="tw-animate-spin tw-text-2xl tw-text-blue-500" />
                    </div>
                ) : categories.length === 0 ? (
                    <div className="tw-text-center tw-py-10 tw-text-gray-400 tw-text-sm">No categories found</div>
                ) : (
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-3">
                        {categories.map(category => (
                            <div key={category.id} className="tw-border tw-border-gray-100 tw-rounded-lg tw-p-3 hover:tw-border-blue-200 tw-transition-colors">
                                <div className="tw-flex tw-items-start tw-justify-between tw-mb-2">
                                    <div className="tw-flex tw-items-center tw-gap-2">
                                        <div className="tw-w-9 tw-h-9 tw-rounded-lg tw-bg-blue-50 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                            <Icon icon={category.icon} className="tw-text-blue-600 tw-text-lg" />
                                        </div>
                                        <div>
                                            <p className="tw-font-medium tw-text-gray-800 tw-text-sm">{category.name}</p>
                                            <p className="tw-text-[10px] tw-text-gray-500">{category.vendorCount} vendors</p>
                                        </div>
                                    </div>
                                    <div className="tw-flex tw-gap-0.5 tw-flex-shrink-0">
                                        <button
                                            onClick={() => handleOpenEdit(category)}
                                            className="tw-p-1.5 tw-rounded hover:tw-bg-blue-50 tw-text-gray-400 hover:tw-text-blue-600"
                                            title="Edit"
                                        >
                                            <Icon icon="mdi:pencil-outline" className="tw-text-sm" />
                                        </button>
                                        <button
                                            onClick={() => setDeleteConfirmId(category.id)}
                                            className="tw-p-1.5 tw-rounded hover:tw-bg-red-50 tw-text-gray-400 hover:tw-text-red-500"
                                            title="Delete"
                                        >
                                            <Icon icon="mdi:delete-outline" className="tw-text-sm" />
                                        </button>
                                    </div>
                                </div>
                                {category.description && (
                                    <p className="tw-text-xs tw-text-gray-500 tw-line-clamp-2">{category.description}</p>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Add / Edit Modal */}
            {modalMode && (
                <div className="tw-fixed tw-inset-0 tw-bg-black/50 tw-flex tw-items-center tw-justify-center tw-z-50">
                    <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-md tw-mx-4">
                        <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-border-b tw-border-gray-100">
                            <h3 className="tw-text-base tw-font-semibold tw-text-gray-800">
                                {modalMode === 'edit' ? 'Edit Category' : 'Add Category'}
                            </h3>
                            <button onClick={handleCloseModal} className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400">
                                <Icon icon="mdi:close" className="tw-text-lg" />
                            </button>
                        </div>
                        <div className="tw-p-4 tw-space-y-3">
                            <div>
                                <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Category Name *</label>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                                    className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                                    placeholder="Enter category name"
                                />
                            </div>
                            <div>
                                <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Description</label>
                                <textarea
                                    value={form.description}
                                    onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                                    rows="2"
                                    className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 tw-resize-none"
                                    placeholder="Category description"
                                />
                            </div>
                        </div>
                        <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-px-4 tw-py-3 tw-border-t tw-border-gray-100 tw-bg-gray-50 tw-rounded-b-xl">
                            <button onClick={handleCloseModal} className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-600 hover:tw-bg-gray-100 tw-rounded-lg">
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={isSaveLoading || !form.name.trim()}
                                className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-rounded-lg disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-flex tw-items-center tw-gap-2"
                            >
                                {isSaveLoading && <Icon icon="mdi:loading" className="tw-animate-spin" />}
                                {modalMode === 'edit' ? 'Update' : 'Add Category'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirmId && (
                <div className="tw-fixed tw-inset-0 tw-bg-black/50 tw-flex tw-items-center tw-justify-center tw-z-50">
                    <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-sm tw-mx-4 tw-p-5">
                        <div className="tw-flex tw-items-center tw-gap-3 tw-mb-3">
                            <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-red-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                <Icon icon="mdi:delete-alert" className="tw-text-red-600 tw-text-xl" />
                            </div>
                            <div>
                                <p className="tw-font-semibold tw-text-gray-800 tw-text-sm">Delete Category</p>
                                <p className="tw-text-xs tw-text-gray-500">This action cannot be undone</p>
                            </div>
                        </div>
                        <p className="tw-text-sm tw-text-gray-600 tw-mb-4">
                            Are you sure you want to delete <span className="tw-font-semibold">{categories.find(c => c.id === deleteConfirmId)?.name}</span>?
                        </p>
                        <div className="tw-flex tw-justify-end tw-gap-2">
                            <button
                                onClick={() => setDeleteConfirmId(null)}
                                disabled={deleteVendorCategoryLoading}
                                className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded-lg disabled:tw-opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleDeleteConfirm(deleteConfirmId)}
                                disabled={deleteVendorCategoryLoading}
                                className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-red-600 hover:tw-bg-red-700 tw-rounded-lg tw-flex tw-items-center tw-gap-2 disabled:tw-opacity-50"
                            >
                                {deleteVendorCategoryLoading && <Icon icon="mdi:loading" className="tw-animate-spin" />}
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </VendorSettingLayout>
    );
};

export default VendorSettingCategories;
